import * as gameSDK from '@fe/game-sdk';
import { type Signal, signal } from '@preact/signals';
import { type App, appConfig, cnAppConfig } from '@pst/app-config';
import { type Language, languageToGameSdk } from './locale/language';

// import zhTW from 'dayjs/locale/zh-tw';
// import zhCN from 'dayjs/locale/zh-cn';
// import en from 'dayjs/locale/en';
// import es from 'dayjs/locale/es';
// import ja from 'dayjs/locale/ja';
// import ko from 'dayjs/locale/ko';
// import ru from 'dayjs/locale/ru';
// import dayjs from 'dayjs';

// const dayjsLocaleMap: Record<Language, ILocale> = {
//   [Language.English]: en,
//   [Language.ZhHant]: zhTW,
//   [Language.Japanese]: ja,
//   [Language.Korea]: ko,
//   [Language.Ru]: ru,
//   [Language.Spanish]: es,
//   [Language.Chinese]: zhCN,
// };

export interface News {
  description: string;
  forward_count: number;
  id: string;
  image: string;
  image_url: string;
  keyword: string;
  like_count: number;
  news_name: string;
  time_zone: number;
  title: string;
  update_at: number;
  url: string;
  content: string;
}

/** 营销平台配置的 channel 类型 */
export enum NewsType {
  News = 1,
  Updates = 2,
}

/**
 * 营销平台配置的 website 类型
 **/
enum WebsiteType {
  PC = 'PC',
  Mobile = 'Mobile',
}

export class PstNews {
  appId: number;
  instance: gameSDK.SiteApi;
  NewsType = NewsType;
  WebsiteType = WebsiteType;
  websiteTypes = {
    [cnAppConfig.appId]: {
      [WebsiteType.PC]: 152,
      [WebsiteType.Mobile]: 153,
    },
    /** !!! 这里配置的是营销平台的新加坡区域配置 */
    [appConfig.appId]: {
      [WebsiteType.PC]: 163,
      [WebsiteType.Mobile]: 164,
    },
  };

  store: {
    loadingNews: Signal<boolean>;
    loadingUpdates: Signal<boolean>;
    news: Signal<News[]>;
    updates: Signal<News[]>;
    newsDetail: Signal<Record<string, News>>;
  };

  constructor({
    language,
    region,
  }: {
    /** 一开始放在了 va */
    region: App['region'];
    language: Language | 'zh-CN';
  }) {
    this.appId = region === 'cn' ? cnAppConfig.appId : appConfig.appId;
    this.store = {
      loadingNews: signal(false),
      loadingUpdates: signal(false),
      news: signal<News[]>([]),
      updates: signal<News[]>([]),
      newsDetail: signal<Record<string, News>>({}),
    };
    const sandBoxHost = 'https://act-sandbox.sgsnssdk.com';
    // 营销没有兼容海外 boe 域名
    const useSandboxHost = window.location.hostname.includes(atob('Ynl0ZWludGwubmV0'));

    this.instance = new gameSDK.SiteApi({
      region,
      appId: this.appId,
      language: languageToGameSdk[language as Language] || language,
      host: useSandboxHost ? sandBoxHost : undefined,
    });
  }

  async getAllNews({ channel, website }: { channel: NewsType; website: WebsiteType }) {
    const websiteForRemote = this.websiteTypes[this.appId][website];
    if (channel === NewsType.News) {
      this.store.loadingNews.value = true;
    }
    if (channel === NewsType.Updates) {
      this.store.loadingUpdates.value = true;
    }

    try {
      const news = await this.instance.getNews({
        channel,
        block: 1,
        website: websiteForRemote,
        page: 1,
        page_index: 1,
        page_size: 999,
      });
      switch (channel) {
        case NewsType.News: {
          this.store.news.value = news;
          break;
        }
        case NewsType.Updates: {
          this.store.updates.value = news;
          break;
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (channel === NewsType.News) {
        this.store.loadingNews.value = false;
      }
      if (channel === NewsType.Updates) {
        this.store.loadingUpdates.value = false;
      }
    }
  }

  async getNewsDetail(id: string) {
    const newsDetail = await this.instance.getNewsDetail({
      id,
    });
    this.store.newsDetail.value = { ...this.store.newsDetail.value, [id]: newsDetail };
    return newsDetail;
  }
}
